import type { ReactNode, SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import MajesticonsBan from '~icons/majesticons/ban'
import MajesticonsExclamation from '~icons/majesticons/exclamation'
import MajesticonsQuestionMarkCircle from '~icons/majesticons/question-mark-circle'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import { cx } from 'src/utils/cx'

export type MediaContentRemovedProps = {
  type: 'standalone' | 'in-grid'
  overlayType?: 'model-cover-under-review' | 'model-cover-warned'
}

function getIcon(overlayType: MediaContentRemovedProps['overlayType']) {
  return match(overlayType)
    .returnType<(props: SVGProps<SVGSVGElement>) => ReactNode>()
    .with('model-cover-under-review', () => MajesticonsExclamation)
    .with('model-cover-warned', () => MajesticonsBan)
    .otherwise(() => MajesticonsQuestionMarkCircle)
}

export default function MediaContentRemoved(props: MediaContentRemovedProps) {
  const { type, overlayType } = props
  const { t } = useTranslation(['common'])

  const icon = getIcon(overlayType)

  return (
    <>
      {icon({
        className: cx(
          'text-neutral-300 dark:text-neutral-100/20',
          type === 'standalone' && 'text-3xl sm:text-8xl',
          type === 'in-grid' && 'text-6xl',
        ),
      })}

      <p
        className={cx(
          'text-center font-light text-neutral-400 dark:text-neutral-100/20 mt-1 whitespace-pre',
          type === 'standalone' && 'text-xl sm:text-3xl',
          type === 'in-grid' && 'text-lg',
        )}
      >
        {t(`common:content-removed.${overlayType}.title`)}
      </p>
      <MarkdownRenderer className="prose text-xs font-normal text-neutral-400 dark:text-neutral-100/20 px-2">
        {t(`common:content-removed.${overlayType}.content`)}
      </MarkdownRenderer>
    </>
  )
}

export function MediaContentRemovedIcon(props: StyleProps) {
  const { className, ...rest } = props
  return (
    <MajesticonsBan
      className={cx('text-neutral-700 dark:text-neutral-300', className)}
      {...rest}
    />
  )
}

export function ArtworkWarnedOverlay(
  props: {
    placement: 'in-grid' | 'in-detail'
    type?: 'artwork-warned'
  } & StyleProps,
) {
  const { t } = useTranslation(['common'])

  if (props.placement === 'in-grid') {
    return (
      <MarkdownRenderer
        className={cx('prose text-xs *:text-white', props.className)}
      >
        {t('common:content-removed.artwork-warned.preview-content')}
      </MarkdownRenderer>
    )
  }
  return (
    <MarkdownRenderer
      className={cx('prose text-xs sm:text-base *:text-white', props.className)}
    >
      {t('common:content-removed.artwork-warned.detail-content')}
    </MarkdownRenderer>
  )
}

export function ArtworkUnderReviewCornerTag(props: StyleProps) {
  const { t } = useTranslation(['common'])
  return (
    <div
      className={cx(
        'bg-red-400/90 rounded-lg text-white font-medium',
        props.className,
      )}
    >
      {t('common:content-removed.artwork-under-review.label')}
    </div>
  )
}
